import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { UserCircle, Linkedin, Sparkles } from "lucide-react";
import { getImageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const PLACEHOLDER = [
  {
    id: "p1",
    name: "Pendiri & Pembina",
    role: "Founder & Advisor",
    bio: "Menginisiasi Sauyunan Foundation berdasarkan filosofi Sunda Silih Asih, Silih Asah, Silih Asuh untuk membangun ekosistem dampak yang menghubungkan komunitas, pemerintah, dan mitra pembangunan.",
    order: 1,
  },
  {
    id: "p2",
    name: "Ketua Yayasan",
    role: "Chair of Foundation",
    bio: "Memimpin arah strategis dan tata kelola organisasi untuk memastikan setiap program dijalankan dengan integritas, akuntabilitas, dan dampak yang terukur.",
    order: 2,
  },
  {
    id: "p3",
    name: "Direktur Program",
    role: "Program Director",
    bio: "Mengoordinasikan 7 bidang program strategis dan memastikan sinergi lintas program untuk menciptakan dampak yang terintegrasi.",
    order: 3,
  },
  {
    id: "p4",
    name: "Direktur Kemitraan",
    role: "Partnership Director",
    bio: "Membangun jejaring kolaborasi dengan pemerintah, dunia usaha, akademisi, komunitas, dan mitra internasional.",
    order: 4,
  },
];

export default function Team() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { settings } = useSettings();
  const pageData = settings?.team_page || {};

  useEffect(() => {
    api.get("/team").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const team = items.length > 0 ? items : PLACEHOLDER;

  return (
    <div data-testid="page-team">
      <SeoHead
        title={{ id: "Tim", en: "Team" }}
        description={{
          id: pageData.description || "Kenali tim Sauyunan Foundation — para pemimpin dan penggerak di balik ekosistem dampak kami.",
          en: "Meet the Sauyunan Foundation team — the leaders driving our impact ecosystem.",
        }}
      />
      <PageHeader
        eyebrow={pageData.eyebrow || "Tim Kami"}
        title={pageData.title || "Para penggerak ekosistem dampak"}
        description={pageData.description || "Tim Sauyunan Foundation terdiri dari para profesional lintas disiplin yang menghubungkan visi filantropi dengan aksi nyata di lapangan."}
        image={pageData.image ? getImageUrl(pageData.image) : undefined}
      />

      <section className="section-y bg-white">
        <div className="container-sf">
          {items.length === 0 && loaded && (
            <div className="mb-8 rounded-2xl bg-[var(--brand-soft-blue)] p-5 flex items-start gap-3 text-sm text-slate-700">
              <Sparkles className="h-5 w-5 text-[var(--brand-blue)] shrink-0 mt-0.5" />
              <div>Profil tim resmi akan segera dipublikasikan. Berikut struktur peran dalam organisasi Sauyunan Foundation.</div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m) => (
              <div key={m.id} className="card-sf overflow-hidden group flex flex-col" data-testid={`team-member-${m.id}`}>
                <div className="aspect-[4/5] bg-gradient-to-br from-[#E7EDFB] to-[#F5F5F5] overflow-hidden">
                  {m.photo_url ? (
                    <img src={getImageUrl(m.photo_url)} alt={m.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <UserCircle className="h-24 w-24 text-slate-300" strokeWidth={1} />
                    </div>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-slate-900 leading-tight">{m.name}</h3>
                  <div className="mt-1 text-sm font-semibold text-[var(--brand-orange)] uppercase tracking-wider">{m.role}</div>
                  {m.bio && <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-4 flex-1">{m.bio}</p>}
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--brand-blue)] hover:text-[var(--brand-orange)]">
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values banner */}
      <section className="pb-24">
        <div className="container-sf">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--brand-blue)] to-[#254DAA] text-white p-10 md:p-14 relative overflow-hidden text-center">
            <div className="grain-overlay" />
            <h3 className="relative text-3xl md:text-4xl font-bold text-white mb-4">Bergabunglah dengan tim kami</h3>
            <p className="relative text-slate-200 max-w-2xl mx-auto mb-6">Kami membuka peluang bagi profesional, peneliti, dan relawan untuk berkontribusi membangun ekosistem dampak Sauyunan.</p>
            <div className="relative flex flex-wrap justify-center gap-3">
              <a href="/volunteer" className="btn-primary">Jadi Relawan</a>
              <a href="/kontak" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">Kirim CV</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
